'use client';

import { ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsDesktop } from '@/shared/lib/react/useIsDesktop';
import ModalHeader from './ModalHeader';

interface ModalProps {
  open: boolean;
  children: ReactNode;
  title?: string;
  titlePosition?: 'center' | 'left' | 'right';
  subTitle?: string;
  rounded?: string;
  onClose: () => void;
}

const BaseModal = ({ open, children, title, titlePosition = 'left', subTitle, rounded = '8px', onClose }: ModalProps) => {
  const isDesktop = useIsDesktop();
  const [lockBodyScroll, setLockBodyScroll] = useState(false);
  const DesktopBackdropMotion = { initial: false, animate: { opacity: 1 }, exit: { opacity: 1 }, transition: { duration: 0 } } as const;
  const MobileBackdropMotion = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.3 } } as const;
  const DesktopPanelMotion = { initial: false, animate: { opacity: 1, y: 0 }, exit: { opacity: 1, y: 0 }, transition: { duration: 0 } } as const;
  const MobilePanelMotion = { initial: { opacity: 0, y: 120 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 120 }, transition: { type: 'tween', ease: 'easeOut', duration: 0.3 } } as const;
  const backdropMotion = isDesktop ? DesktopBackdropMotion : MobileBackdropMotion;
  const panelMotion = isDesktop ? DesktopPanelMotion : MobilePanelMotion;

  useEffect(() => {
    if (open) setLockBodyScroll(true);
  }, [open]);

  useEffect(() => {
    if (lockBodyScroll) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [lockBodyScroll]);

  return (
    <AnimatePresence onExitComplete={() => setLockBodyScroll(false)}>
      {open && (
        <div className="fixed w-full mx-auto bottom-0 right-0 left-0 z-[101] h-screen">
          <motion.div className="bottom-0 left-0 right-0 top-0 w-full h-full bg-opacity-50 bg-black z-[102]" onClick={onClose} {...backdropMotion} />
          <motion.div
            className="absolute bottom-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-[101] bg-white flex flex-col w-full md:w-auto md:max-w-[90vw] max-h-[90vh]"
            style={{ borderRadius: rounded }}
            onClick={(e) => e.stopPropagation()}
            {...panelMotion}
          >
            <ModalHeader title={title} titlePosition={titlePosition} subTitle={subTitle} className="pt-[2rem] px-[1rem] pb-[0.88rem] md:pt-[1.5rem] md:px-[2rem] md:pb-[1rem]" onClose={onClose} />
            <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BaseModal;
